import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import './Field.css';

export interface FieldProps {
  label: string;
  /** One short line under the control; an error replaces it. */
  hint?: string | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  /** Receives the id to attach to the control. */
  children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
}

/** Label, control, hint or error: the one shape every form in Paige uses (react-hook-form + zod). */
export function Field({ label, hint, error, required, children }: FieldProps): ReactNode {
  const id = useId();
  const descId = `${id}-desc`;
  const text = error ?? hint;
  return (
    <div className={['pui-field', error && 'pui-field--error'].filter(Boolean).join(' ')}>
      <label htmlFor={id} className="pui-field__label">
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      {children({ id, describedBy: text ? descId : undefined })}
      {text ? (
        <p id={descId} className="pui-field__text" role={error ? 'alert' : undefined}>
          {text}
        </p>
      ) : null}
    </div>
  );
}

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, ...rest },
  ref,
) {
  return (
    <input ref={ref} className={['pui-input', className].filter(Boolean).join(' ')} {...rest} />
  );
});

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, children, ...rest },
  ref,
) {
  return (
    <select ref={ref} className={['pui-input', className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </select>
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={['pui-input', 'pui-input--area', className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
});

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
}

/** A checkbox with its label; used for notification kinds and switches in settings. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, className, ...rest },
  ref,
) {
  return (
    <label className={['pui-checkbox', className].filter(Boolean).join(' ')}>
      <input ref={ref} type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  );
});
